import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  memo,
} from "react";
import {
  INVENTORY_UNIT_FILTER_STORAGE,
  getParent9263FirestoreId,
} from "../utils/inventoryUnitFilter";

const PARENT_UNIT_FILTER_LABEL = "הכל";

/**
 * Multi-select unit filter: real units (Firestore doc id) + מחסן.
 * "הכל" selects / clears all units (מחסן unchanged).
 */
function InventoryUnitMultiSelect({ units, selectedIds, onChange }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const selectAllCheckboxRef = useRef(null);

  const parent9263Id = useMemo(() => getParent9263FirestoreId(units), [units]);

  const allUnitIds = useMemo(
    () => (units || []).map((u) => u.id).filter(Boolean),
    [units],
  );

  const allUnitsSelected = useMemo(
    () =>
      allUnitIds.length > 0 &&
      allUnitIds.every((id) => selectedIds.includes(id)),
    [allUnitIds, selectedIds],
  );

  const someUnitsSelected = useMemo(
    () => allUnitIds.some((id) => selectedIds.includes(id)),
    [allUnitIds, selectedIds],
  );

  useEffect(() => {
    const el = selectAllCheckboxRef.current;
    if (!el) return;
    el.indeterminate = someUnitsSelected && !allUnitsSelected;
  }, [someUnitsSelected, allUnitsSelected]);

  const sortedChildUnits = useMemo(() => {
    const list = [...(units || [])];
    return list
      .filter((u) => u.id !== parent9263Id)
      .sort((a, b) =>
        String(a.unitName || "").localeCompare(String(b.unitName || ""), "he"),
      );
  }, [units, parent9263Id]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const toggle = useCallback(
    (id) => {
      const next = new Set(selectedIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      onChange([...next]);
    },
    [selectedIds, onChange],
  );

  const toggleAllUnits = useCallback(() => {
    if (allUnitsSelected) {
      onChange(selectedIds.filter((id) => !allUnitIds.includes(id)));
    } else {
      onChange([...new Set([...selectedIds, ...allUnitIds])]);
    }
  }, [allUnitsSelected, allUnitIds, selectedIds, onChange]);

  const clear = useCallback(() => onChange([]), [onChange]);

  const summaryLabel = useMemo(() => {
    if (!selectedIds.length) return "כל היחידות";
    const hasStorage = selectedIds.includes(INVENTORY_UNIT_FILTER_STORAGE);
    if (allUnitsSelected) {
      if (!hasStorage) return PARENT_UNIT_FILTER_LABEL;
      return `${PARENT_UNIT_FILTER_LABEL} + מחסן`;
    }
    if (selectedIds.length === 1) {
      if (hasStorage) return "מחסן";
      const u = units.find((x) => x.id === selectedIds[0]);
      if (!u) return "יחידה";
      if (parent9263Id && u.id === parent9263Id) return PARENT_UNIT_FILTER_LABEL;
      return u.unitName || "יחידה";
    }
    return `${selectedIds.length} יחידות`;
  }, [
    selectedIds,
    units,
    parent9263Id,
    allUnitsSelected,
  ]);

  return (
    <div className="inventory-unit-multiselect" ref={rootRef}>
      <button
        type="button"
        className="inventory-unit-multiselect-trigger filter-select"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="inventory-unit-multiselect-trigger-text">
          {summaryLabel}
        </span>
        <span className="inventory-unit-multiselect-caret" aria-hidden>
          ▼
        </span>
      </button>
      {open ? (
        <div className="inventory-unit-multiselect-panel" role="listbox">
          {allUnitIds.length > 0 ? (
            <label
              className="inventory-unit-multiselect-option inventory-unit-multiselect-option-pinned"
            >
              <input
                ref={selectAllCheckboxRef}
                type="checkbox"
                checked={allUnitsSelected}
                onChange={toggleAllUnits}
              />
              <span>{PARENT_UNIT_FILTER_LABEL}</span>
            </label>
          ) : null}
          <label className="inventory-unit-multiselect-option">
            <input
              type="checkbox"
              checked={selectedIds.includes(INVENTORY_UNIT_FILTER_STORAGE)}
              onChange={() => toggle(INVENTORY_UNIT_FILTER_STORAGE)}
            />
            <span>מחסן (זמינים במלאי)</span>
          </label>
          <div className="inventory-unit-multiselect-divider" />
          {sortedChildUnits.map((u) => (
            <label key={u.id} className="inventory-unit-multiselect-option">
              <input
                type="checkbox"
                checked={selectedIds.includes(u.id)}
                onChange={() => toggle(u.id)}
              />
              <span>{u.unitName || u.id}</span>
            </label>
          ))}
          {selectedIds.length > 0 ? (
            <button
              type="button"
              className="inventory-unit-multiselect-clear"
              onClick={clear}
            >
              נקה בחירה
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default memo(InventoryUnitMultiSelect);
